import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    installActionKey,
    installHowToKey,
    resolveInstallOffer,
} from '../../domain/pwa/installOffer';
import { NOTIFICATION_STATUS, resolveNotificationOffer } from '../../domain/pwa/notificationOffer';
import { translate } from '../../domain/i18n/messages';
import {
    getPlantNotificationCapability,
    requestPlantNotificationPermission,
    showAppNotification,
} from '../notifications/plantNotificationCapability';
import { checkForPlantArrivals, ensureBackgroundArrivalSync } from '../notifications/plantArrivalWatch';
import { promptForInstall, subscribeInstallPromptState } from './installPrompt';
import {
    countVisit,
    readInstallCompletedAt,
    readInstallDismissedAt,
    readNotificationDismissedAt,
    writeInstallDismissed,
    writeNotificationDismissed,
} from './offerPreferences';

/**
 * Which offer, if any, this visitor should see right now (SPEC-016).
 *
 * One hook for both because the two are not independent: install outranks
 * notifications, and on iOS it is a precondition rather than a preference —
 * Safari exposes no Notification API in a browser tab, only once the app is on
 * the home screen. Deciding them separately would put a notification card in
 * front of iOS visitors who have no way to accept it.
 *
 * At most one offer is ever returned. Two cards asking for two things is the
 * shape this feature exists to avoid.
 */

export function useAppOffers({
    savedPlantCount = 0,
    localeCode = '',
    languageCode = 'en',
    now = null,
} = {}) {
    const [promptState, setPromptState] = useState({ support: 'unsupported', isStandalone: false });
    const [notificationStatus, setNotificationStatus] = useState(
        () => getPlantNotificationCapability().status,
    );
    // Counted once per page load, in an effect rather than in render — a render
    // can run twice under StrictMode, and a visit that counts twice reaches the
    // install threshold half a visit early.
    const [visitCount, setVisitCount] = useState(0);
    /**
     * The stored dismissal stamps, read once and then kept in step here.
     *
     * State rather than a read inside the memo below: a memo that reaches into
     * storage is not a function of its dependencies, so it would go stale the
     * moment anything else wrote a stamp, and it would read Web Storage on every
     * render of every screen that carries the offer.
     */
    const [stamps, setStamps] = useState(() => ({
        installDismissed: readInstallDismissedAt(),
        notifyDismissed: readNotificationDismissedAt(),
        installed: readInstallCompletedAt(),
    }));
    /**
     * Dismissed during THIS mount, before the re-read below has caught up.
     *
     * Only the notification card needs it: dismissing writes a stamp and the
     * card goes on the next render either way, but the state change is what
     * triggers that render. The install card's "already installed" case is a
     * STORED fact rather than one of these, because it has to survive the
     * unmount that a plant detail page causes — see `INSTALL_COMPLETED_KEY`.
     */
    const [hidden, setHidden] = useState({ notify: false });
    // One clock for the whole mount. Holds are measured in weeks, so re-reading
    // it every render would buy nothing and make both memos recompute forever.
    const [mountedAt] = useState(() => Date.now());
    const evaluatedAt = now === null ? mountedAt : now;

    useEffect(() => {
        setVisitCount(countVisit());
    }, []);

    useEffect(() => subscribeInstallPromptState((nextPromptState) => {
        setPromptState(nextPromptState);
        // `appinstalled` reaches us through here, and it is what records the
        // install — including one done from the browser's own menu, which this
        // app never sees a prompt for.
        setStamps((current) => ({ ...current, installed: readInstallCompletedAt() }));
    }), []);

    const install = useMemo(() => resolveInstallOffer({
        support: promptState.support,
        visitCount,
        dismissedAt: stamps.installDismissed,
        installedAt: stamps.installed,
        now: evaluatedAt,
    }), [evaluatedAt, promptState.support, stamps.installDismissed, stamps.installed, visitCount]);

    const notify = useMemo(() => resolveNotificationOffer({
        status: notificationStatus,
        visitCount,
        savedPlantCount,
        dismissedAt: stamps.notifyDismissed,
        now: evaluatedAt,
        isInstallOfferShowing: install.canOffer,
    }), [evaluatedAt, install.canOffer, notificationStatus, savedPlantCount, stamps.notifyDismissed, visitCount]);

    const acceptInstall = useCallback(async () => {
        const { outcome } = await promptForInstall();

        // `unavailable` is every platform without `beforeinstallprompt`, where
        // the card's instruction IS the answer and must stay on screen.
        // `promptForInstall` has already recorded an accepted install; this
        // picks the stamp up so the card goes now rather than on the next mount.
        if (outcome === 'accepted') {
            setStamps((current) => ({ ...current, installed: readInstallCompletedAt() }));
        }

        return outcome;
    }, []);

    const dismissInstall = useCallback(() => {
        const at = Date.now();

        // Written before the state change, so a reload that beats the next paint
        // still agrees with what the visitor just pressed.
        writeInstallDismissed(at);
        setStamps((current) => ({ ...current, installDismissed: at }));
    }, []);

    /**
     * Ask for permission, and prove the channel works.
     *
     * The confirmation notification is not decoration. A visitor who has just
     * granted permission has no other way to find out whether it took — the next
     * real notification may be weeks away, on the next deploy that adds a plant —
     * and a permission that silently failed looks exactly like one that worked.
     */
    const enableNotifications = useCallback(async () => {
        const capability = await requestPlantNotificationPermission();

        setNotificationStatus(capability.status);

        if (capability.status !== NOTIFICATION_STATUS.granted) {
            return capability.status;
        }

        // Seeds the seen list before anything can be announced, so granting
        // permission can never itself produce a pile of arrival notifications.
        await checkForPlantArrivals({ localeCode, languageCode });
        await ensureBackgroundArrivalSync();
        await showAppNotification({
            title: translate(languageCode, 'pwa.notify.confirmed.title'),
            body: translate(languageCode, 'pwa.notify.confirmed.body'),
            tag: 'plant-notifications-on',
            url: localeCode ? `/${localeCode}/plants` : '/',
        });

        return capability.status;
    }, [languageCode, localeCode]);

    const dismissNotifications = useCallback(() => {
        const at = Date.now();

        writeNotificationDismissed(at);
        setStamps((current) => ({ ...current, notifyDismissed: at }));
        setHidden((current) => ({ ...current, notify: true }));
    }, []);

    const offer = (() => {
        // Nothing at all until the visit count is known. It is filled by an
        // effect, so on the very first render it is 0 while the saved-plant
        // count — which `useGarden` reads synchronously — may already be 1: the
        // notification card would paint for one frame and be replaced by the
        // install card on the next.
        if (visitCount === 0) {
            return null;
        }

        if (install.canOffer) {
            return 'install';
        }

        return notify.canOffer && !hidden.notify ? 'notify' : null;
    })();

    return {
        offer,
        installActionKey: installActionKey(promptState.support),
        installHowToKey: installHowToKey(promptState.support),
        notificationStatus,
        acceptInstall,
        dismissInstall,
        enableNotifications,
        dismissNotifications,
    };
}
